import { useMemo } from "react";
import { useCustom } from "@refinedev/core";
import {
  Clock,
  ArrowRight,
  MessageSquare,
  UserPlus,
  UserMinus,
  CreditCard,
  CircleCheck,
  CircleX,
  RotateCcw,
  DollarSign,
  Pin,
  PlusCircle,
  Award,
  Link2,
  ExternalLink,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { getStaffActionLabel } from "@/lib/translations";

interface HistoryItem {
  id: number;
  orderId: number;
  adminUserId: number | null;
  action: string;
  previousValue: unknown;
  newValue: unknown;
  fromStatusId: number | null;
  toStatusId: number | null;
  createdAt: string;
  adminUser?: {
    id: number;
    username: string;
  } | null;
  fromStatus?: {
    id: number;
    code: string;
    name: string;
    color: string;
  } | null;
  toStatus?: {
    id: number;
    code: string;
    name: string;
    color: string;
  } | null;
}

interface OrderHistoryProps {
  orderId: number;
}

// Узкий тип для JSON-объекта в previousValue/newValue.
// Не используем индексную сигнатуру с unknown в публичной сигнатуре —
// доступ к полям через геттер getField.
type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };
interface JsonObject {
  [key: string]: JsonValue;
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getField(obj: JsonObject, key: string): JsonValue | undefined {
  return Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
}

function asText(value: JsonValue | undefined): string | null {
  if (value === undefined || value === null) { return null; }
  if (typeof value === "object") { return null; }
  return String(value);
}

// Категория события → цвет акцента иконки/линии
type ActionTone = "neutral" | "success" | "danger" | "info" | "warning";

const ACTION_TONE: Record<string, ActionTone> = {
  CREATED: "info",
  STATUS_CHANGED: "info",
  ASSIGNED: "info",
  UNASSIGNED: "neutral",
  NOTE_ADDED: "neutral",
  NOTE_UPDATED: "neutral",
  NOTE_DELETED: "neutral",
  NOTE_PINNED: "neutral",
  NOTE_UNPINNED: "neutral",
  CHAT_LINKED: "neutral",
  CHAT_UNLINKED: "neutral",
  ITEM_ADDED: "neutral",
  ITEM_REMOVED: "neutral",
  ITEM_PRICE_CHANGED: "warning",
  BONUS_VALUE_CHANGED: "warning",
  PAYMENT_CREATED: "info",
  PAYMENT_CONFIRMED: "success",
  PAYMENT_REJECTED: "danger",
  PAYMENT_REFUNDED: "warning",
  PAYMENT_LINK_CREATED: "info",
  PAYMENT_LINK_OPENED: "info",
  PAYMENT_ATTEMPT_FAILED: "danger",
  ORDER_VIEWED_BY_USER: "neutral",
};

const TONE_CLASSES: Record<ActionTone, { dot: string; ring: string }> = {
  neutral: { dot: "bg-muted text-muted-foreground", ring: "border-muted" },
  success: { dot: "bg-green-100 text-green-700", ring: "border-green-300" },
  danger: { dot: "bg-red-100 text-red-700", ring: "border-red-300" },
  info: { dot: "bg-blue-100 text-blue-700", ring: "border-blue-300" },
  warning: { dot: "bg-amber-100 text-amber-700", ring: "border-amber-300" },
};

function getActionIcon(action: string) {
  switch (action) {
    case "STATUS_CHANGED":
      return <ArrowRight className="h-3 w-3" />;
    case "NOTE_ADDED":
    case "NOTE_UPDATED":
    case "NOTE_DELETED":
      return <MessageSquare className="h-3 w-3" />;
    case "NOTE_PINNED":
    case "NOTE_UNPINNED":
      return <Pin className="h-3 w-3" />;
    case "ASSIGNED":
      return <UserPlus className="h-3 w-3" />;
    case "UNASSIGNED":
      return <UserMinus className="h-3 w-3" />;
    case "CREATED":
      return <PlusCircle className="h-3 w-3" />;
    case "PAYMENT_CREATED":
      return <CreditCard className="h-3 w-3" />;
    case "PAYMENT_CONFIRMED":
      return <CircleCheck className="h-3 w-3" />;
    case "PAYMENT_REJECTED":
      return <CircleX className="h-3 w-3" />;
    case "PAYMENT_REFUNDED":
      return <RotateCcw className="h-3 w-3" />;
    case "ITEM_PRICE_CHANGED":
      return <DollarSign className="h-3 w-3" />;
    case "BONUS_VALUE_CHANGED":
      return <Award className="h-3 w-3" />;
    case "PAYMENT_LINK_CREATED":
      return <Link2 className="h-3 w-3" />;
    case "PAYMENT_LINK_OPENED":
      return <ExternalLink className="h-3 w-3" />;
    case "PAYMENT_ATTEMPT_FAILED":
      return <AlertTriangle className="h-3 w-3" />;
    case "ORDER_VIEWED_BY_USER":
      return <Eye className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
}

const PROVIDER_LABELS: Record<string, string> = {
  cloudpayments: "CloudPayments",
  multicard: "Multicard",
};

function providerLabel(value: JsonValue | undefined): string {
  const text = asText(value) ?? "";
  return PROVIDER_LABELS[text] ?? text;
}

function renderActionDetails(item: HistoryItem) {
  const newVal = isJsonObject(item.newValue) ? item.newValue : null;
  const prevVal = isJsonObject(item.previousValue) ? item.previousValue : null;

  if (item.action === "PAYMENT_LINK_CREATED" && newVal) {
    const delivery = asText(getField(newVal, "delivery"));
    const deliveryLabel: Record<string, string> = {
      copy: "ссылка скопирована",
      telegram: "отправлено в Telegram",
      email: "отправлено на email",
    };
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5">
        {providerLabel(getField(newVal, "provider"))}
        {delivery ? ` · ${deliveryLabel[delivery] ?? delivery}` : null}
      </div>
    );
  }

  if (item.action === "PAYMENT_ATTEMPT_FAILED" && newVal) {
    const reason = asText(getField(newVal, "reason"));
    return (
      <div className="text-[11px] text-red-600 mt-0.5">
        {providerLabel(getField(newVal, "provider"))}
        {reason ? ` · ${reason}` : null}
      </div>
    );
  }

  if (item.action.startsWith("PAYMENT_") && newVal) {
    const amount = asText(getField(newVal, "amount"));
    const currency = asText(getField(newVal, "currency"));
    const provider = getField(newVal, "provider");
    const paymentId = asText(getField(newVal, "paymentId"));
    const parts: string[] = [];
    if (paymentId) { parts.push(`#${paymentId}`); }
    if (amount && currency) { parts.push(`${amount} ${currency}`); }
    if (provider !== undefined && provider !== null) {
      parts.push(providerLabel(provider));
    }
    if (parts.length === 0) { return null; }
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5">
        {parts.join(" · ")}
      </div>
    );
  }

  if (item.action === "ITEM_PRICE_CHANGED" && prevVal && newVal) {
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5">
        {asText(getField(prevVal, "unitPrice"))} →{" "}
        {asText(getField(newVal, "unitPrice"))}
      </div>
    );
  }

  if (item.action === "BONUS_VALUE_CHANGED" && prevVal && newVal) {
    const bonusName =
      asText(getField(newVal, "bonusName")) ??
      asText(getField(prevVal, "bonusName"));
    const prevValue = asText(getField(prevVal, "value"));
    const newValue = asText(getField(newVal, "value"));
    const actionType = asText(getField(newVal, "action"));
    const actionLabels: Record<string, string> = {
      increment: "+1",
      decrement: "−1",
      set: "установка",
      push: "добавление",
      remove: "удаление",
    };
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5">
        {bonusName ? <span className="font-medium">{bonusName}</span> : null}{" "}
        {prevValue} → {newValue}
        {actionType
          ? ` (${actionLabels[actionType] ?? actionType})`
          : null}
      </div>
    );
  }

  if (item.action === "CREATED" && newVal) {
    const total = asText(getField(newVal, "total"));
    const currency = asText(getField(newVal, "currency"));
    const itemsCount = asText(getField(newVal, "itemsCount"));
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5">
        {total} {currency} · {itemsCount} поз.
      </div>
    );
  }

  return null;
}

function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDayKey(value: string): string {
  return new Date(value).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (sameDay(date, today)) { return "Сегодня"; }
  if (sameDay(date, yesterday)) { return "Вчера"; }
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function OrderHistory({ orderId }: OrderHistoryProps) {
  const historyQuery = useCustom<HistoryItem[]>({
    url: `/orders/${orderId}/history`,
    method: "get",
  });

  const isLoading = historyQuery.query?.isLoading ?? false;
  const history = useMemo(
    () => historyQuery.query?.data?.data ?? [],
    [historyQuery.query?.data?.data],
  );

  // Группировка по дню (история приходит desc по createdAt — порядок сохраняем)
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: HistoryItem[] }>();
    for (const item of history) {
      const key = formatDayKey(item.createdAt);
      let group = map.get(key);
      if (!group) {
        group = { label: formatDayLabel(item.createdAt), items: [] };
        map.set(key, group);
      }
      group.items.push(item);
    }
    return Array.from(map.values());
  }, [history]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8 text-sm">
        История пуста
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.label} className="space-y-0">
          <div className="sticky top-0 z-10 -mx-1 mb-2 bg-background/95 px-1 py-1 backdrop-blur">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </span>
          </div>

          <div className="space-y-0">
            {group.items.map((item) => {
              const tone = ACTION_TONE[item.action] ?? "neutral";
              const toneClass = TONE_CLASSES[tone];
              const isClientAction =
                item.action === "ORDER_VIEWED_BY_USER" ||
                item.action === "PAYMENT_LINK_OPENED";
              return (
                <div
                  key={item.id}
                  className={`relative flex gap-3 border-l-2 ${toneClass.ring} pb-3 pl-4`}
                >
                  <div
                    className={`absolute -left-[11px] top-0 flex h-5 w-5 items-center justify-center rounded-full ${toneClass.dot}`}
                  >
                    {getActionIcon(item.action)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-sm font-medium">
                        {getStaffActionLabel(item.action)}
                      </span>
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {formatTime(item.createdAt)}
                      </span>
                      {item.adminUser ? (
                        <Badge
                          variant="secondary"
                          className="px-1.5 py-0 text-[10px]"
                        >
                          {item.adminUser.username}
                        </Badge>
                      ) : isClientAction ? (
                        <Badge
                          variant="outline"
                          className="px-1.5 py-0 text-[10px]"
                        >
                          клиент
                        </Badge>
                      ) : null}
                    </div>

                    {item.action === "STATUS_CHANGED" &&
                      item.fromStatus &&
                      item.toStatus && (
                        <div className="mt-1 flex items-center gap-1">
                          <Badge
                            variant="outline"
                            className="px-1.5 py-0 text-[11px]"
                            style={{ borderColor: item.fromStatus.color }}
                          >
                            {item.fromStatus.name}
                          </Badge>
                          <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />
                          <Badge
                            variant="outline"
                            className="px-1.5 py-0 text-[11px]"
                            style={{ borderColor: item.toStatus.color }}
                          >
                            {item.toStatus.name}
                          </Badge>
                        </div>
                      )}

                    {renderActionDetails(item)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
